import React, { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { CheckoutForm as CheckoutFormType } from '../types';
import CheckoutForm from '../components/checkout/CheckoutForm';
import RegionSelector from '../components/checkout/RegionSelector';
import PrivacyLawSelector from '../components/checkout/PrivacyLawSelector';
import DpiaLawSelector from '../components/checkout/DpiaLawSelector';
import PiaLawSelector from '../components/checkout/PiaLawSelector';
import PaymentSummary from '../components/checkout/PaymentSummary';
import CreditCardForm from '../components/checkout/CreditCardForm';
import { Assessment } from '../types';
import { supabase } from '../lib/supabase';

interface CheckoutContentProps {
  assessment: Assessment;
}

export default function CheckoutContent({ assessment }: CheckoutContentProps) {
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedLaws, setSelectedLaws] = useState<string[]>([]);
  const [selectedDpiaLaws, setSelectedDpiaLaws] = useState<string[]>([]);
  const [selectedPiaLaws, setSelectedPiaLaws] = useState<string[]>([]);
  const [additionalDetails, setAdditionalDetails] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CheckoutFormType | null>(null);
  
  const stripe = useStripe();
  const elements = useElements();

  const handleFormSubmit = async (data: CheckoutFormType) => {
    setFormData(data);
    
    try {
      // Create order record
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert({
          assessment_type: assessment.id,
          price: assessment.price,
          business_description: additionalDetails,
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
          title: data.title,
          company: data.company,
          company_address: data.companyAddress,
          phone: data.phone,
          website: data.website,
          accepted_terms: data.acceptTerms,
          accepted_privacy: data.acceptPrivacy,
          accepted_consent: data.acceptConsent
        })
        .select()
        .single();

      if (orderError) throw orderError;

      // Create order regions records
      const orderRegions = selectedRegions.map(regionId => ({
        order_id: order.id,
        region_id: regionId,
        countries: selectedCountries[regionId] || []
      }));

      const { error: regionsError } = await supabase
        .from('order_regions')
        .insert(orderRegions);

      if (regionsError) throw regionsError;
    } catch (error) {
      console.error('Error saving order:', error);
      setError('Failed to save order information. Please try again.');
      return;
    }
  };

  const handleRegionsChange = (regions: string[]) => {
    setSelectedRegions(regions);
  };

  const handlePayment = async () => {
    if (!stripe || !elements || !formData) {
      setError('Please fill in all required information');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const { error: cardError } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement)!,
        billing_details: {
          email: formData.email,
          name: `${formData.firstName} ${formData.lastName}`,
        },
      });

      if (cardError) {
        throw new Error(cardError.message);
      }

      setIsProcessing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed');
      setIsProcessing(false);
    }
  };

  return (
    <div className="grid md:grid-cols-3 gap-8">
      <div className="md:col-span-2 space-y-6">
        <RegionSelector 
          selectedRegions={selectedRegions}
          onRegionsChange={handleRegionsChange}
          additionalDetails={additionalDetails}
          onDetailsChange={setAdditionalDetails}
        />
        {assessment.id === 'pia' && (
          <PiaLawSelector
            selectedLaws={selectedPiaLaws}
            onLawsChange={setSelectedPiaLaws}
          />
        )}
        {assessment.id === 'dpia' && (
          <DpiaLawSelector
            selectedLaws={selectedDpiaLaws}
            onLawsChange={setSelectedDpiaLaws}
          />
        )}
        {assessment.id === 'pga' && (
          <PrivacyLawSelector
            selectedLaws={selectedLaws}
            onLawsChange={setSelectedLaws}
          />
        )}
        <CheckoutForm onSubmit={handleFormSubmit} />
        <CreditCardForm error={error} />
      </div>
      
      <div className="md:col-span-1">
        <PaymentSummary 
          assessment={assessment}
          selectedRegions={selectedRegions}
          isProcessing={isProcessing}
          onSubmit={handlePayment}
        />
      </div>
    </div>
  );
}